import { FC, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { toastCaller } from '@/shared/ui';

import { analyticsModelModel, AnalyticsModel } from '@/entities/analyticsModel';

import { AnalyticsModelForm } from '@/features/analyticsModelEditor';

import { i18n } from '../../config';
import { ANALYTICS_TAB_PATH, buildAnalyticsUrl } from '../../lib';

type AnalyticsEditorPaneProps = {
  className?: string;
};

export const AnalyticsEditorPane: FC<AnalyticsEditorPaneProps> = (props) => {
  const { className } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();

  const editorMode = searchParams.get('mode');
  const modelId = searchParams.get('id') ?? undefined;
  const isEditing = editorMode === 'edit' && Boolean(modelId);

  const model = useAppSelector((state) =>
    modelId
      ? analyticsModelModel.selectors.selectAnalyticsModelById(modelId)(state)
      : undefined,
  );
  const isModelLoading = useAppSelector(
    modelId
      ? analyticsModelModel.selectors.selectAnalyticsModelByIdLoading(modelId)
      : () => false,
  );
  const error = useAppSelector(
    analyticsModelModel.selectors.selectAnalyticsModelsError,
  );

  // A refresh that fails while the editor already shows the model has nowhere
  // to render its error, so the toast is the only way to tell the author their
  // copy is stale.
  const shownModel = useRef<AnalyticsModel | undefined>(undefined);

  useEffect(() => {
    shownModel.current = model;
  }, [model]);

  useEffect(() => {
    if (!modelId) {
      return;
    }

    dispatch(analyticsModelModel.fetchAnalyticsModelById(modelId))
      .unwrap()
      .catch(() => {
        if (shownModel.current) {
          toastCaller({
            type: 'error',
            message: formatMessage(i18n.analyticsEditorMessages.loadError),
          });
        }
      });
  }, [modelId]);

  const handleBackToList = useCallback(() => {
    navigate(ANALYTICS_TAB_PATH);
  }, [navigate]);

  const handleSaveComplete = useCallback(
    (saved: AnalyticsModel) => {
      navigate(buildAnalyticsUrl({ mode: 'edit', id: saved.id }));
    },
    [navigate],
  );

  const handleDeleteComplete = useCallback(() => {
    navigate(ANALYTICS_TAB_PATH);
  }, [navigate]);

  const handleCloned = useCallback(
    (newModelId: string) => {
      navigate(buildAnalyticsUrl({ mode: 'edit', id: newModelId }));
    },
    [navigate],
  );

  // Rendering the form before the edited model arrives would offer an empty
  // draft, and saving that draft creates a second model instead of updating
  // the one the URL names.
  if (isEditing && !model) {
    return (
      <div className={className}>
        {error && !isModelLoading ? (
          <FormattedMessage {...i18n.analyticsEditorMessages.loadError} />
        ) : (
          <FormattedMessage id="global.loading" defaultMessage="Loading…" />
        )}
      </div>
    );
  }

  return (
    <AnalyticsModelForm
      className={className}
      model={isEditing ? model : undefined}
      onBackToList={handleBackToList}
      onCloned={handleCloned}
      onDeleteComplete={handleDeleteComplete}
      onSaveComplete={handleSaveComplete}
    />
  );
};
